import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSaveLink } from '../../hooks/useSaveLink'
import { useAuth } from '../../hooks/useAuth'

interface ShareEntry {
  id: string
  url: string
  title?: string
  text?: string
  createdAt: string
  files?: Array<{ name: string; type: string }>
  debug?: {
    endpoint: string
    method: string
    fields: Array<
      | { key: string; type: 'value'; value: string }
      | { key: string; type: 'file'; name: string; fileType: string; size: number }
    >
  }
}

type ShareStatus = 'loading' | 'saving' | 'success' | 'error'

function ShareComplete() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const shareId = searchParams.get('id') ?? undefined

  const [shareEntry, setShareEntry] = useState<ShareEntry | null>(null)
  const [status, setStatus] = useState<ShareStatus>('loading')
  const [message, setMessage] = useState('Preparing your link…')
  const [hasRequestedQueue, setHasRequestedQueue] = useState(false)

  const saveLink = useSaveLink()
  const { isAuthenticated, isReady, signInWithGoogle, isAuthenticating, requiresConfiguration } = useAuth()

  const shareWorkerSupported = useMemo(() => 'serviceWorker' in navigator, [])

  useEffect(() => {
    if (!shareWorkerSupported) {
      setStatus('error')
      setMessage('Service workers are not supported in this browser.')
      return
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data
      if (!data || typeof data !== 'object') {
        return
      }

      if (data.type === 'SHARE_QUEUE') {
        const queue = (data.payload as ShareEntry[]) ?? []
        const entry = shareId ? queue.find((item) => item.id === shareId) : queue[0]

        if (!entry) {
          setStatus('error')
          setMessage('No shared link was found. Try sharing the page again.')
          return
        }

        setShareEntry(entry)
      }

      if (data.type === 'SHARE_QUEUE_UPDATED') {
        const entry = data.payload as ShareEntry
        if (!shareId || entry.id === shareId) {
          setShareEntry(entry)
        }
      }
    }

    navigator.serviceWorker.addEventListener('message', handleMessage)

    let removeControllerListener: (() => void) | undefined

    if (!hasRequestedQueue) {
      const requestQueue = () => {
        if (!navigator.serviceWorker.controller) {
          return false
        }
        navigator.serviceWorker.controller.postMessage({ type: 'GET_SHARE_QUEUE' })
        setHasRequestedQueue(true)
        return true
      }

      if (!requestQueue()) {
        navigator.serviceWorker
          .ready
          .then(() => {
            if (requestQueue()) {
              return
            }

            const onControllerChange = () => {
              if (requestQueue()) {
                navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
                removeControllerListener = undefined
              }
            }

            navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
            removeControllerListener = () =>
              navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
          })
          .catch(() => {
            setStatus('error')
            setMessage('Unable to communicate with the service worker. Try opening the app directly and refresh.')
          })
      }
    }

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage)
      removeControllerListener?.()
    }
  }, [shareId, hasRequestedQueue, shareWorkerSupported])

  useEffect(() => {
    if (!shareEntry || !isAuthenticated) {
      return
    }

    if (!shareEntry.url) {
      setStatus('error')
      setMessage('Unable to read the shared URL. Please try again.')
      return
    }

    if (status === 'success' || status === 'saving') {
      return
    }

    setStatus('saving')
    setMessage('Saving the shared link…')

    saveLink.mutate(
      {
        url: shareEntry.url,
        title: shareEntry.title || undefined,
        note: shareEntry.text || undefined,
      },
      {
        onSuccess: () => {
          setStatus('success')
          setMessage('Link saved! Summaries will appear once processing is complete.')
          navigator.serviceWorker.controller?.postMessage({ type: 'CONSUME_SHARE_ENTRY', id: shareEntry.id })
        },
        onError: (error) => {
          setStatus('error')
          setMessage(error instanceof Error ? error.message : 'Failed to save the shared link.')
        },
      },
    )
  }, [saveLink, shareEntry, status, isAuthenticated])

  const handleBackToDashboard = () => {
    navigate('/')
  }

  const renderDetails = () => {
    if (!shareEntry) {
      return null
    }

    return (
      <dl style={{ marginTop: '16px', color: '#4f5b66', fontSize: '0.95rem' }}>
        <div style={{ marginBottom: '8px' }}>
          <dt style={{ fontWeight: 600 }}>URL</dt>
          <dd>
            <a href={shareEntry.url} target="_blank" rel="noreferrer" style={{ color: '#0b1f33' }}>
              {shareEntry.url}
            </a>
          </dd>
        </div>
        {shareEntry.title ? (
          <div style={{ marginBottom: '8px' }}>
            <dt style={{ fontWeight: 600 }}>Title</dt>
            <dd>{shareEntry.title}</dd>
          </div>
        ) : null}
        {shareEntry.text ? (
          <div style={{ marginBottom: '8px' }}>
            <dt style={{ fontWeight: 600 }}>Notes</dt>
            <dd>{shareEntry.text}</dd>
          </div>
        ) : null}
        {shareEntry.files?.length ? (
          <div>
            <dt style={{ fontWeight: 600 }}>Attachments</dt>
            <dd>{shareEntry.files.map((file) => file.name).join(', ')}</dd>
          </div>
        ) : null}
        {shareEntry.debug ? (
          <div>
            <dt style={{ fontWeight: 600 }}>Debug</dt>
            <dd>
              <pre
                style={{
                  marginTop: '8px',
                  padding: '12px',
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  fontSize: '0.8rem',
                  overflowX: 'auto',
                }}
              >
                {JSON.stringify(shareEntry.debug, null, 2)}
              </pre>
            </dd>
          </div>
        ) : null}
      </dl>
    )
  }

  const renderAuthCallout = () => {
    if (!isReady) {
      return (
        <div style={{ marginTop: '16px', padding: '16px', background: '#f1f5f9', borderRadius: '8px', color: '#475569' }}>
          Initialising authentication…
        </div>
      )
    }

    if (isAuthenticated) {
      return null
    }

    return (
      <div style={{ marginTop: '16px', padding: '16px', background: '#e0f2fe', borderRadius: '8px', color: '#0f172a', display: 'grid', gap: '12px' }}>
        <div>
          <p style={{ fontWeight: 600 }}>Sign in to finish saving</p>
          <p style={{ fontSize: '0.95rem' }}>
            Authenticate so the shared link can be sent to your backend.
          </p>
        </div>
        <button
          type='button'
          onClick={signInWithGoogle}
          disabled={isAuthenticating}
          style={{
            justifySelf: 'start',
            padding: '10px 18px',
            borderRadius: '8px',
            border: 'none',
            background: requiresConfiguration ? '#0b1f33' : '#0ea5e9',
            color: '#f8fafc',
            fontWeight: 600,
            cursor: isAuthenticating ? 'not-allowed' : 'pointer',
          }}
        >
          {isAuthenticating ? 'Connecting…' : requiresConfiguration ? 'Use demo account' : 'Sign in'}
        </button>
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: '16px', maxWidth: '640px' }}>
      <header>
        <h1 style={{ fontSize: '2rem', marginBottom: '12px' }}>Share complete</h1>
        <p style={{ color: '#4f5b66' }}>
          Links shared from other apps appear here first and automatically move into your saved items queue.
        </p>
      </header>

      <section style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 12px 24px -20px rgba(11,31,51,0.5)' }}>
        <p style={{ fontSize: '1.1rem', fontWeight: 600, color: status === 'error' ? '#c0353b' : '#0b1f33' }}>{message}</p>
        {renderDetails()}
        {renderAuthCallout()}

        {status === 'loading' || status === 'saving' ? (
          <p style={{ marginTop: '16px', color: '#4f5b66' }}>You can return to the dashboard—this page will close automatically.</p>
        ) : null}

        {status === 'success' ? (
          <div style={{ marginTop: '20px' }}>
            <button
              type="button"
              onClick={handleBackToDashboard}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: 'none',
                background: '#0b1f33',
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Go to dashboard
            </button>
          </div>
        ) : null}

        {status === 'error' ? (
          <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleBackToDashboard}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: 'none',
                background: '#c0353b',
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Back to dashboard
            </button>
            <span style={{ alignSelf: 'center', color: '#4f5b66' }}>
              Try sharing again or submit the link manually on the dashboard.
            </span>
          </div>
        ) : null}
      </section>
    </div>
  )
}

export default ShareComplete
